import * as React from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import { MainContent } from '../components/layout/MainContent';
import { PageContentRow } from '../components/layout/PageContentRow';
import { useFetchBrowse } from '../hooks/useFetchData';
import { useNodes } from '../hooks/useNodes';
import Page from '../components/layout/Page';
import PageContentWrapper from '../components/layout/PageContentWrapper';
import TopicContent from '../components/TopicContent';
import styled from 'styled-components';
import { getSiblings } from '../helpers/node';
import Sidebar from '../components/Sidebar';
import { useTranslation } from '@/hooks/useTranslation';
import { useMetaTitle } from '@/hooks/useMetaTitle';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { RootNodeContext } from '@/context/RootNodeContext';
import Loader from '@/components/Loader';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.screens.l}) {
    flex-direction: row;
    padding: ${({ theme }) => `0 ${theme.space.md}`};
    width: 100%;
  }
`;

const SidebarWrapper = styled.div`
  order: 1;
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => `0 -${theme.space.s}`};
  background-color: ${({ theme }) => theme.colors.elvitaLighterGray};
  padding: ${({ theme }) =>
    `18px ${theme.space.s} ${theme.space.xl} ${theme.space.s}`};

  @media (min-width: ${({ theme }) => theme.screens.md}) {
    flex-direction: row;
    padding: ${({ theme }) =>
      `18px ${theme.space.md} ${theme.space.xl} ${theme.space.md}`};
  }

  @media (min-width: ${({ theme }) => theme.screens.l}) {
    background-color: transparent;
    flex-direction: column;
    width: 28%;
    padding: ${({ theme }) =>
      `${theme.space.xs} ${theme.space.s} ${theme.space.xl} ${theme.space.s}`};
  }
`;

const Topic: React.FC = () => {
  const { trackPageView } = useMatomo();
  const { topicId, path } = useParams<{ topicId: string; path: string }>();
  const { data, isPending: isLoading } = useFetchBrowse();
  const { t } = useTranslation();
  useMetaTitle();

  const getTopicState = React.useCallback(() => {
    if (!topicId) return null;

    if (!data?.data || data.data.length === 0) {
      const cached = sessionStorage.getItem(`topic_state_${topicId}`);
      return cached ? JSON.parse(cached) : null;
    }
    const rootNode = data.data[0];
    const topLevel = path
      ? rootNode.children?.find(item => item.topicId === path)
      : undefined;

    const siblings =
      getSiblings(topicId, rootNode).filter(
        (sibling: Elvita.Node) => sibling && sibling?.topicId,
      ) ?? [];

    const state = { topLevel, siblings, rootNode };
    sessionStorage.setItem(`topic_state_${topicId}`, JSON.stringify(state));
    return state;
  }, [data, topicId, path]);

  const topicState = React.useMemo(() => getTopicState(), [getTopicState]);

  const topLevel = topicState?.topLevel;
  const siblings = topicState?.siblings || [];
  const rootNode = topicState?.rootNode;

  const { breadcrumbs } = useNodes(
    topLevel,
    topicId,
    path,
    topLevel?.title ?? '',
  );

  React.useEffect(() => {
    trackPageView({});
  }, [topicId, trackPageView]);

  if (!topicId) return null;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <RootNodeContext.Provider value={rootNode}>
      <Page>
        <PageContentWrapper flex={1}>
          <PageContentRow $center={true}>
            <MainContent>
              <Breadcrumbs
                breadcrumbs={breadcrumbs.slice(0, -1)}
                hideLast={true}
              />
            </MainContent>
          </PageContentRow>
          <ContentWrapper>
            <TopicContent topicId={topicId} />
            <SidebarWrapper>
              {siblings.length > 0 && (
                <Sidebar items={siblings} title={t('topic.siblingContent')} />
              )}
              <div
                id="related"
                style={{
                  flex: 1,
                }}
              />
            </SidebarWrapper>
          </ContentWrapper>
        </PageContentWrapper>
      </Page>
    </RootNodeContext.Provider>
  );
};

export default Topic;
